/**
 * RunTheater — a real-3D miniature workshop that acts out a run, live.
 *
 * Takes the exact props RunTrace takes, so both mount sites (LiveRunView for
 * streamed sessions, RunView for polled/replayed records) feed it with zero
 * extra wiring: `events` is the adapted TraceEvent list (cumulative — the
 * Director dedupes), `baseMs` anchors the wall clock, `live` switches live vs
 * finished-tableau behavior, `nowMs` is the host's 1 Hz tick for text.
 *
 * The heavy WebGL layer (three.js) is lazy-loaded, so run pages only download
 * it when the theater is actually visible; the caption bar renders instantly.
 * A brand-new event stream (different first beat — e.g. navigating from run A
 * to run B in the same mounted view) resets the Director defensively even if
 * the mount site forgot a React `key`.
 */

import type { TraceEvent } from '@evt/flow-stream-ui'
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { SilentBoundary, supportsWebGL } from '../components/WebglGuard'
import { usePrefersReducedMotion } from '../hooks/use-prefers-reduced-motion'
import { useI18n } from '../i18n'
import { beatKey, eventsToBeats } from './beats'
import { Director } from './director'
import type { ReplayState } from './scene3d/TheaterCanvas'
import type { TheaterFrame } from './types'
import './theater.css'

const TheaterCanvas = lazy(() => import('./scene3d/TheaterCanvas'))

export interface RunTheaterProps {
  events: TraceEvent[]
  baseMs: number
  live: boolean
  nowMs: number
}

const COLLAPSE_KEY = 'portal.theater.collapsed'

export function RunTheater({ events, baseMs, live, nowMs }: RunTheaterProps) {
  const { t } = useI18n()
  const beats = useMemo(() => eventsToBeats(events, t.beats), [events, t])
  const reducedMotion = usePrefersReducedMotion()

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1')
  // Probed once per page; turned off for good when the scene throws (a context refused or lost mid-run).
  const [canDraw, setCanDraw] = useState(supportsWebGL)
  const [meta, setMeta] = useState<{ caption: string; tone: TheaterFrame['tone']; busy: boolean; replaying: boolean }>({
    caption: t.beats.settingTheStage,
    tone: 'idle',
    busy: false,
    replaying: false
  })

  // The Director keeps the locale it was built with: swapping it on a locale
  // change would replay the whole scene. Already-queued captions stay in the
  // old language; everything newly derived (beats above, journey, status bar)
  // follows the switch immediately.
  const directorRef = useRef<Director | null>(null)
  if (directorRef.current === null) directorRef.current = new Director({ reducedMotion, strings: t.beats })
  const replayRef = useRef<ReplayState | null>(null)
  const firstBeatKeyRef = useRef<string | null>(null)

  // Defensive stream-identity check: a different run's events (new first beat)
  // means a fresh Director — seqs restart per run and would collide in dedupe.
  const firstKey = beats[0] ? beatKey(beats[0]) : null
  if (firstKey !== firstBeatKeyRef.current) {
    if (firstBeatKeyRef.current !== null) {
      directorRef.current = new Director({ reducedMotion, strings: t.beats })
      replayRef.current = null
    }
    firstBeatKeyRef.current = firstKey
  }

  // Ingest the cumulative beat list (deduped inside the Director).
  useEffect(() => {
    if (replayRef.current) return // the canvas feeds the director during replay
    directorRef.current?.ingest(beats, Date.now())
  })

  // Elapsed clock for the caption + the wall clock inside the scene.
  const elapsedMs = elapsedFor(live, nowMs, baseMs, events)
  const elapsedRef = useRef(elapsedMs)
  elapsedRef.current = elapsedMs

  const onToggle = (): void => {
    const next = !collapsed
    setCollapsed(next)
    localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
  }

  const onReplay = (): void => {
    const director = new Director({ reducedMotion, strings: t.beats })
    // Consume the first-ingest catch-up so the replay animates from the top.
    director.ingest([], Date.now())
    directorRef.current = director
    replayRef.current = { queue: [...beats], nextAt: Date.now() }
    setMeta((m) => ({ ...m, replaying: true }))
  }

  const canReplay = !live && !meta.replaying && beats.length >= 4

  return (
    <section
      className={`theater-card${collapsed ? ' collapsed' : ''}`}
      data-tone={meta.tone}
      aria-label={t.theater.runVisualization}
    >
      <div className="theater-head">
        <span className={`theater-dot tone-${meta.tone}`} />
        <span className="theater-caption" aria-live="polite">
          {meta.caption}
        </span>
        <span className="theater-meta">
          {formatElapsed(elapsedMs)}
          {canReplay && (
            <button type="button" className="theater-btn" onClick={onReplay}>
              {t.theater.replay}
            </button>
          )}
          <button type="button" className="theater-btn" onClick={onToggle}>
            {collapsed ? t.theater.showWorkshop : t.theater.hide}
          </button>
        </span>
      </div>
      {!collapsed && canDraw && (
        <div className="theater-stage">
          {/* A scene that cannot draw takes only itself down: the caption stays, and the run page around it. */}
          <SilentBoundary onError={() => setCanDraw(false)}>
            <Suspense fallback={<div className="theater-loading">{t.theater.warmingUp}</div>}>
              <TheaterCanvas
                directorRef={directorRef}
                replayRef={replayRef}
                elapsedRef={elapsedRef}
                reducedMotion={reducedMotion}
                strings={{ overflowMore: t.theater.overflowMore, stageLoading: t.theater.stageLoading }}
                onMeta={setMeta}
              />
            </Suspense>
          </SilentBoundary>
        </div>
      )}
    </section>
  )
}

/** Elapsed for the caption clock: live → wall clock, finished → last event. */
function elapsedFor(live: boolean, nowMs: number, baseMs: number, events: TraceEvent[]): number {
  if (live) return Math.max(0, nowMs - baseMs)
  let last = baseMs
  for (const e of events) {
    if (Number.isFinite(e.atMs) && e.seq !== Number.MAX_SAFE_INTEGER && e.atMs > last) last = e.atMs
  }
  return Math.max(0, last - baseMs)
}

function formatElapsed(ms: number): string {
  const totalS = Math.floor(ms / 1000)
  const m = Math.floor(totalS / 60)
  const s = totalS % 60
  if (m >= 60) {
    const h = Math.floor(m / 60)
    return `${h}h ${(m % 60).toString().padStart(2, '0')}m`
  }
  return `${m}:${s.toString().padStart(2, '0')}`
}
